import { Box, Button, Card, CardContent, CardHeader, Chip, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material/styles"
import type { LeaderboardEntry, SortByType } from "@crm-src/types/financial-metrics"
import { formatCurrency } from "@crm-src/lib/utils"
import { TrendingUp, TrendingDown, Award, DollarSign } from "lucide-react"

interface SalespeoplePerformanceListProps {
  data: LeaderboardEntry[]
  sortBy: SortByType
  onSortChange: (sortBy: SortByType) => void
  onSelectSalesperson: (userId: number) => void
  isLoading?: boolean
}

export function SalespeoplePerformanceList({
  data,
  sortBy,
  onSortChange,
  onSelectSalesperson,
  isLoading
}: SalespeoplePerformanceListProps) {
  // Medal tiers keep their literal gold/silver/bronze hex (decorative ranking
  // colors, not semantic palette roles); everyone else gets the brand-tinted
  // pill. Returns an `sx` to apply to the rank Chip.
  const getRankBadgeSx = (rank: number): SxProps<Theme> => {
    if (rank === 1) return { bgcolor: "#eab308", color: "#fff" }
    if (rank === 2) return { bgcolor: "#9ca3af", color: "#fff" }
    if (rank === 3) return { bgcolor: "#b45309", color: "#fff" }
    return { bgcolor: (t: Theme) => t.palette.action.selected, color: "primary.main" }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <Skeleton variant="rounded" sx={{ height: 36, width: 128 }} />
          <Skeleton variant="rounded" sx={{ height: 36, width: 160 }} />
          <Skeleton variant="rounded" sx={{ height: 36, width: 128 }} />
        </Box>
        <Card>
          <CardContent sx={{ pt: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} variant="rounded" sx={{ height: 64 }} />
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>
    )
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Sort Controls */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography component="span" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>Sort by:</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant={sortBy === 'revenue' ? 'contained' : 'outlined'}
            color={sortBy === 'revenue' ? 'primary' : 'inherit'}
            size="small"
            onClick={() => onSortChange('revenue')}
            startIcon={<DollarSign size={16} />}
          >
            Revenue
          </Button>
          <Button
            variant={sortBy === 'conversion_rate' ? 'contained' : 'outlined'}
            color={sortBy === 'conversion_rate' ? 'primary' : 'inherit'}
            size="small"
            onClick={() => onSortChange('conversion_rate')}
            startIcon={<TrendingUp size={16} />}
          >
            Conversion Rate
          </Button>
          <Button
            variant={sortBy === 'total_deals' ? 'contained' : 'outlined'}
            color={sortBy === 'total_deals' ? 'primary' : 'inherit'}
            size="small"
            onClick={() => onSortChange('total_deals')}
            startIcon={<Award size={16} />}
          >
            Total Deals
          </Button>
        </Box>
      </Box>

      {/* Leaderboard Table */}
      <Card>
        <CardHeader
          title={<Typography variant="h6">Sales Team Performance</Typography>}
          subheader="Click on any salesperson to view their detailed performance metrics"
        />
        <CardContent>
          {data.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 64 }}>Rank</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                    <TableCell align="right">Deals Closed</TableCell>
                    <TableCell align="right">Conversion Rate</TableCell>
                    <TableCell align="right">Avg Deal Size</TableCell>
                    <TableCell align="right">Quotes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((salesperson) => (
                    <TableRow
                      key={salesperson.user_id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => onSelectSalesperson(salesperson.user_id)}
                    >
                      <TableCell>
                        <Chip size="small" label={`#${salesperson.rank}`} sx={getRankBadgeSx(salesperson.rank)} />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography sx={{ fontWeight: 500 }}>{salesperson.user_name}</Typography>
                          <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>{salesperson.user_email}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: "success.main" }}>
                        {formatCurrency(salesperson.total_revenue, { decimals: 0 })}
                      </TableCell>
                      <TableCell align="right">
                        {salesperson.total_deals_closed}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                          {salesperson.conversion_rate}%
                          {salesperson.conversion_rate >= 50 ? (
                            <Box component="span" sx={{ display: "flex", color: "success.main" }}><TrendingUp size={16} /></Box>
                          ) : salesperson.conversion_rate >= 30 ? (
                            <Box component="span" sx={{ display: "flex", color: "warning.main" }}><TrendingUp size={16} /></Box>
                          ) : (
                            <Box component="span" sx={{ display: "flex", color: "error.main" }}><TrendingDown size={16} /></Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(salesperson.average_deal_size, { decimals: 0 })}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ fontSize: "0.875rem" }}>
                          <Box>{salesperson.total_quotes_created} total</Box>
                          <Box sx={{ color: "text.secondary" }}>
                            {salesperson.quotes_pending} pending
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2, opacity: 0.3 }}><Award size={48} /></Box>
              <Typography>No salespeople data available for the selected period</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {data.length > 0 && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>
          <Card>
            <CardHeader
              title={<Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.secondary" }}>Total Team Revenue</Typography>}
            />
            <CardContent>
              <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "success.main" }}>
                {formatCurrency(
                  data.reduce((sum, s) => {
                    const revenue = Number(s.total_revenue) || 0
                    return sum + (isNaN(revenue) ? 0 : revenue)
                  }, 0),
                  { decimals: 0 }
                )}
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardHeader
              title={<Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.secondary" }}>Average Conversion Rate</Typography>}
            />
            <CardContent>
              <Typography sx={{ fontSize: "1.5rem", fontWeight: 700 }}>
                {(() => {
                  const totalRate = data.reduce((sum, s) => {
                    const rate = Number(s.conversion_rate) || 0
                    return sum + (isNaN(rate) ? 0 : rate)
                  }, 0)
                  const avgRate = data.length > 0 ? totalRate / data.length : 0
                  return isNaN(avgRate) ? '0.0' : avgRate.toFixed(1)
                })()}%
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardHeader
              title={<Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.secondary" }}>Total Deals Closed</Typography>}
            />
            <CardContent>
              <Typography sx={{ fontSize: "1.5rem", fontWeight: 700 }}>
                {data.reduce((sum, s) => {
                  const deals = Number(s.total_deals_closed) || 0
                  return sum + (isNaN(deals) ? 0 : deals)
                }, 0)}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  )
}
